import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { 
  ThumbsUp, ThumbsDown, MessageSquare, Send, CheckCircle,
  AlertCircle, Star, FileText, Eye, X, Loader2
} from 'lucide-react';
import { FEEDBACK_CONFIG } from '../../data/governanceContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * DocFeedbackWidget - "Was this helpful?" widget for documentation sections
 */
export function DocFeedbackWidget({ docId, docTitle, portal }) {
  const [feedback, setFeedback] = useState(null); // 'helpful' | 'not-helpful'
  const [showDetails, setShowDetails] = useState(false);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleQuickFeedback = (isHelpful) => {
    setFeedback(isHelpful ? 'helpful' : 'not-helpful');
    if (!isHelpful) {
      setShowDetails(true);
    } else {
      // Auto-submit positive feedback
      submitFeedback({ helpful: true, rating: 5 });
    }
  };

  const submitFeedback = async (data) => {
    setSubmitting(true);
    const feedbackPayload = {
      portal,
      docId,
      type: data.rating ? 'rating' : 'helpful',
      helpful: data.helpful !== undefined ? data.helpful : (feedback === 'helpful'),
      rating: data.rating !== undefined ? data.rating : (rating || null),
      comment: data.comment !== undefined ? data.comment : (comment || null),
      metadata: {}
    };

    try {
      // Submit to backend API
      const response = await fetch(`${API_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackPayload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      // Fallback to localStorage if API fails
      const existingFeedback = JSON.parse(localStorage.getItem('doc-feedback') || '[]');
      existingFeedback.push({ ...feedbackPayload, timestamp: new Date().toISOString() });
      localStorage.setItem('doc-feedback', JSON.stringify(existingFeedback));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitDetails = () => {
    submitFeedback({ helpful: feedback === 'helpful', rating, comment });
  };

  if (submitted) {
    return (
      <Card className="bg-emerald-50 border-emerald-200" data-testid="feedback-submitted">
        <CardContent className="py-4 text-center">
          <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="font-medium text-emerald-800">Thank you for your feedback!</p>
          <p className="text-sm text-emerald-600">Your input helps us improve our documentation.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-gray-200" data-testid="feedback-widget">
      <CardContent className="py-4">
        {!feedback ? (
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-3">Was this documentation helpful?</p>
            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => handleQuickFeedback(true)}
                className="gap-2 hover:bg-emerald-50 hover:border-emerald-300"
              >
                <ThumbsUp className="w-4 h-4" />
                Yes
              </Button>
              <Button
                variant="outline"
                onClick={() => handleQuickFeedback(false)}
                className="gap-2 hover:bg-red-50 hover:border-red-300"
              >
                <ThumbsDown className="w-4 h-4" />
                No
              </Button>
            </div>
          </div>
        ) : showDetails ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-medium text-gray-900">Tell us more</p>
              <Button variant="ghost" size="sm" onClick={() => setShowDetails(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Star Rating */}
            <div>
              <p className="text-sm text-gray-600 mb-2">How would you rate this documentation?</p>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRating(star)}
                    className="p-1 hover:scale-110 transition-transform"
                  >
                    <Star
                      className={`w-6 h-6 ${
                        star <= rating
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {FEEDBACK_CONFIG.ratingScale.labels[rating]}
                </p>
              )}
            </div>

            {/* Comment */}
            <div>
              <p className="text-sm text-gray-600 mb-2">What could be improved?</p>
              <Textarea
                placeholder="Your feedback helps us improve..."
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>

            <Button 
              onClick={handleSubmitDetails} 
              disabled={submitting}
              className="w-full"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Feedback
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="font-medium text-emerald-800">Thanks for the feedback!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * DocIssueReport - Report documentation issues
 */
export function DocIssueReport({ docId, docTitle, portal, isOpen, onClose }) {
  const [issueType, setIssueType] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!issueType || !description) return;

    setSubmitting(true);
    try {
      const issuePayload = {
        portal,
        docId,
        type: 'issue',
        category: issueType,
        comment: description,
        email: email || null,
        metadata: {}
      };

      // Submit to backend API
      const response = await fetch(`${API_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(issuePayload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit issue');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit issue:', error);
      // Fallback to localStorage if API fails
      const existingIssues = JSON.parse(localStorage.getItem('doc-issues') || '[]');
      existingIssues.push({ docId, portal, issueType, description, email, timestamp: new Date().toISOString() });
      localStorage.setItem('doc-issues', JSON.stringify(existingIssues));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="issue-report-modal">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Report Documentation Issue
              </CardTitle>
              <CardDescription>Help us improve: {docTitle}</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-semibold text-lg mb-2">Issue Reported</h3>
              <p className="text-gray-600 mb-4">Thank you for helping improve our documentation!</p>
              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Issue Type */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Issue Type *</p>
                <div className="flex flex-wrap gap-2">
                  {FEEDBACK_CONFIG.issueTypes.map(type => (
                    <Badge
                      key={type.id}
                      variant={issueType === type.id ? 'default' : 'outline'}
                      className={`cursor-pointer ${
                        issueType === type.id ? '' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => setIssueType(type.id)}
                    >
                      {type.label}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Description *</p>
                <Textarea
                  placeholder="Please describe the issue in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Email (optional) */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-gray-400">(optional, for follow-up)</span>
                </p>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={!issueType || !description || submitting}
                  className="flex-1"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * SuggestionForm - Submit documentation suggestions
 */
export function SuggestionForm({ portal, isOpen, onClose }) {
  const [suggestion, setSuggestion] = useState('');
  const [category, setCategory] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { id: 'new-topic', label: 'New Topic' },
    { id: 'more-detail', label: 'More Detail' },
    { id: 'video', label: 'Video Request' },
    { id: 'example', label: 'More Examples' },
    { id: 'translation', label: 'Translation' },
    { id: 'other', label: 'Other' }
  ];

  const handleSubmit = async () => {
    if (!suggestion || !category) return;

    setSubmitting(true);
    try {
      const suggestionPayload = {
        portal,
        docId: 'general',
        type: 'suggestion',
        category,
        comment: suggestion,
        email: email || null,
        metadata: {}
      };

      // Submit to backend API
      const response = await fetch(`${API_URL}/api/feedback/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(suggestionPayload)
      });

      if (!response.ok) {
        throw new Error('Failed to submit suggestion');
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
      // Fallback to localStorage if API fails
      const existing = JSON.parse(localStorage.getItem('doc-suggestions') || '[]');
      existing.push({ portal, category, suggestion, email, timestamp: new Date().toISOString() });
      localStorage.setItem('doc-suggestions', JSON.stringify(existing));
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="suggestion-form-modal">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-indigo-500" />
                Suggest Documentation
              </CardTitle>
              <CardDescription>What would you like to see?</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="font-semibold text-lg mb-2">Suggestion Received</h3>
              <p className="text-gray-600 mb-4">We'll review your suggestion and consider it for future updates!</p>
              <Button onClick={onClose}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Category */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Category *</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => (
                    <Badge
                      key={cat.id}
                      variant={category === cat.id ? 'default' : 'outline'}
                      className={`cursor-pointer ${
                        category === cat.id ? '' : 'hover:bg-gray-100'
                      }`}
                      onClick={() => setCategory(cat.id)}
                    >
                      {cat.label}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Suggestion */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Your Suggestion *</p>
                <Textarea
                  placeholder="Describe what you'd like to see in our documentation..."
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  rows={4}
                />
              </div>

              {/* Email */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-gray-400">(optional)</span>
                </p>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={!suggestion || !category || submitting}
                  className="flex-1"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * FeedbackSummary - Inline feedback collection 
 */
export function FeedbackSummary() {
  // Get stored feedback for display (would be API call in production)
  const feedback = JSON.parse(localStorage.getItem('doc-feedback') || '[]');
  const issues = JSON.parse(localStorage.getItem('doc-issues') || '[]');
  const suggestions = JSON.parse(localStorage.getItem('doc-suggestions') || '[]');

  return (
    <Card className="bg-indigo-50 border-indigo-200">
      <CardContent className="py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-indigo-900">Your Contributions</p>
            <p className="text-sm text-indigo-700">Thank you for helping improve our docs!</p>
          </div>
          <div className="flex gap-4 text-center">
            <div>
              <p className="text-xl font-bold text-indigo-600">{feedback.length}</p>
              <p className="text-xs text-indigo-500">Feedback</p>
            </div>
            <div>
              <p className="text-xl font-bold text-indigo-600">{issues.length}</p>
              <p className="text-xs text-indigo-500">Issues</p>
            </div>
            <div>
              <p className="text-xl font-bold text-indigo-600">{suggestions.length}</p>
              <p className="text-xs text-indigo-500">Suggestions</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default DocFeedbackWidget;
